<template>
  <div class="border-t border-gray-100 first:border-t-0" :role="tableRole('rowgroup')">
    <div :class="compact ? 'flex flex-wrap items-start gap-x-3 gap-y-1' : ['grid gap-3', SHIPMENT_GRID]" class="items-start py-2.5" :role="tableRole('row')">
      <span :role="tableRole('cell')">
        <span class="block font-semibold tabular-nums" :class="state === 'late' ? 'text-red-700' : 'text-gray-900'">{{ fmtDay(shipment.shippedOn ?? shipment.plannedDate) }}</span>
        <span v-if="after > 0" class="block text-xs font-semibold text-red-700">{{ after }} d after requested</span>
      </span>
      <span :class="compact ? '' : 'text-right'" :role="tableRole('cell')">
        <span class="tabular-nums text-gray-900">{{ fmtPcs(pieces) }}</span>
        <span class="mx-1 text-gray-400">·</span>
        <button type="button" class="text-blue-700 hover:underline" :aria-expanded="linesOpen" @click="linesOpen = !linesOpen">{{ linesLabel(shipment.lines.length) }}</button>
      </span>
      <span :role="tableRole('cell')">
        <span class="badge w-fit font-semibold" :class="SHIPMENT_STATE_TONE[state]">{{ STATE_LABEL[state] }}</span>
      </span>
      <span class="text-xs text-gray-600" :class="compact ? 'order-last w-full' : ''" :role="tableRole('cell')">
        <template v-if="event">
          {{ event.forwarder }} ·
          <button v-if="event.hawb" type="button" class="font-mono text-blue-700 hover:underline" @click="emit('shipping-details', event)">{{ event.hawb }}</button>
          <template v-else>
            <span class="text-gray-500">No HAWB/BOL yet</span>
            <template v-if="partner">
              ·
              <button type="button" class="font-semibold text-blue-700 hover:underline" @click="emit('shipping-details', event)">Add HAWB/BOL</button>
            </template>
          </template>
          <span class="mt-0.5 block">
            <template v-if="latestPackingList">
              Packing list:
              <button type="button" class="text-blue-700 hover:underline" @click="emit('shipping-details', event)">{{ latestPackingList.fileName }}</button>
              <span v-if="event.packingLists.length > 1" class="text-gray-500">(v{{ latestPackingList.version }})</span>
            </template>
            <template v-else>
              <span class="font-semibold text-amber-800">Packing list missing</span>
              <template v-if="partner">
                ·
                <button type="button" class="font-semibold text-blue-700 hover:underline" @click="emit('shipping-details', event)">Add packing list</button>
              </template>
            </template>
          </span>
        </template>
        <span v-else-if="state !== 'shipped'" :class="state === 'late' ? 'font-semibold text-red-700' : ''">{{ plannedDetailText(shipment.plannedDate, today) }}</span>
      </span>
      <span v-if="!compact || shipment.note" class="break-words text-xs text-gray-600" :class="compact ? 'order-last w-full' : ''" :role="tableRole('cell')">
        {{ shipment.note }}
      </span>
      <span class="flex justify-end gap-2" :class="compact ? 'ml-auto' : ''" :role="tableRole('cell')">
        <template v-if="partner && state !== 'shipped'">
          <button type="button" class="btn-primary btn-sm" @click="emit('mark-shipped', shipment)">Mark shipped</button>
          <button v-if="canEdit" type="button" class="btn-secondary btn-sm" @click="emit('edit', shipment)">Edit</button>
        </template>
      </span>
    </div>
    <div v-if="linesOpen" :role="tableRole('row')">
      <div :role="tableRole('cell')" :aria-colspan="compact ? undefined : SHIPMENT_COLUMN_COUNT">
        <!-- One grid for the whole list so the quantity sits right after the longest part number, aligned across rows. -->
        <ul class="m-0 mb-2 grid list-none grid-cols-[40px_auto_auto] justify-start gap-x-4 gap-y-1 rounded-lg bg-white p-2 text-xs">
          <li v-for="l in shipment.lines" :key="l.odooLineId" class="contents">
            <span class="tabular-nums text-gray-500">{{ l.lineNo }}</span>
            <span class="flex min-w-0 max-w-xl flex-wrap items-baseline gap-x-2">
              <span
                class="break-all font-mono text-gray-900"
                :class="{ 'cursor-help underline decoration-gray-300 decoration-dotted underline-offset-2': poLines.get(l.odooLineId)?.description }"
                :title="poLines.get(l.odooLineId)?.description"
              >
                {{ l.partNumber ?? '—' }}
                <span v-if="poLines.get(l.odooLineId)?.description" class="sr-only">, {{ poLines.get(l.odooLineId)?.description }}</span>
              </span>
              <span v-if="!poLines.get(l.odooLineId)" class="font-semibold text-amber-800">No longer on the PO</span>
            </span>
            <span class="text-right tabular-nums">
              {{ fmtPcs(l.quantity) }}
              <template v-if="poLines.get(l.odooLineId)">of {{ fmtPcs(poLines.get(l.odooLineId)?.ordered ?? 0) }}</template>
            </span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';

  import { SHIPMENT_COLUMN_COUNT, SHIPMENT_GRID } from '@/components/poTracker/poTrackerGrid';
  import { SHIPMENT_STATE_TONE } from '@/components/poTracker/poTrackerStyles';
  import type { PoLineQuantities, Shipment, ShippingEvent } from '@/types/poTracker';
  import { daysAfterRequested, fmtDay, fmtPcs, linesLabel, plannedDetailText, shipmentState, type ShipmentState } from '@/utils/poTracker';

  const props = defineProps<{ shipment: Shipment; lines: PoLineQuantities[]; event: ShippingEvent | null; today: string; partner: boolean; canEdit: boolean; compact: boolean }>();
  const emit = defineEmits<{ edit: [shipment: Shipment]; 'mark-shipped': [shipment: Shipment]; 'shipping-details': [event: ShippingEvent] }>();

  const STATE_LABEL: Record<ShipmentState, string> = { planned: 'Planned', late: 'Late', shipped: 'Shipped' };

  const linesOpen = ref(false);
  const tableRole = (role: string): string | undefined => (props.compact ? undefined : role);

  const state = computed(() => shipmentState(props.shipment, props.today));
  const after = computed(() => daysAfterRequested(props.shipment));
  const pieces = computed(() => props.shipment.lines.reduce((sum, l) => sum + l.quantity, 0));
  const poLines = computed(() => new Map(props.lines.map((l) => [l.odooLineId, l])));
  const latestPackingList = computed(
    () => props.event?.packingLists.reduce<ShippingEvent['packingLists'][number] | null>((a, b) => (!a || b.version > a.version ? b : a), null) ?? null
  );
</script>
